// @ts-check
import { v4 as uuidv4 } from 'uuid';
import { TrackInsertMarkName, TrackDeleteMarkName } from '../constants.js';
import { findTrackedMarkBetween } from './findTrackedMarkBetween.js';

/**
 * Mark insertion.
 * @param {object} options Mark insertion options.
 * @param {import('prosemirror-state').Transaction} options.tr Transaction.
 * @param {number} options.from From position.
 * @param {number} options.to To position.
 * @param {import('../../../core/types/EditorConfig.js').User} options.user User object ({ name, email }).
 * @param {string} options.date Date.
 * @param {string} [options.id] Optional ID to use (for replace operations where insertion and deletion share the same ID).
 * @returns {import('prosemirror-model').Mark} Insertion mark.
 */
export const markInsertion = ({ tr, from, to, user, date, id: providedId }) => {
  tr.removeMark(from, to, tr.doc.type.schema.marks[TrackDeleteMarkName]);
  tr.removeMark(from, to, tr.doc.type.schema.marks[TrackInsertMarkName]);

  const trackedMark =
    /** @type {{ from: number, to: number, mark: import('prosemirror-model').Mark } | null | undefined} */ (
      findTrackedMarkBetween({
        tr,
        from,
        to,
        markName: TrackInsertMarkName,
        attrs: { authorEmail: user.email || '' },
      })
    );

  let id;
  if (providedId) {
    // Use the provided ID (for replace operations)
    id = providedId;
  } else if (trackedMark) {
    id = trackedMark.mark.attrs.id;
  } else {
    id = uuidv4();
  }

  const insertionMark = tr.doc.type.schema.marks[TrackInsertMarkName].create({
    id,
    author: user.name || '',
    authorEmail: user.email || '',
    authorImage: user.image || '',
    date,
  });

  tr.addMark(from, to, insertionMark);

  // Add insertion mark also to block nodes (figures, text blocks) but not table cells/rows and lists.
  tr.doc.nodesBetween(from, to, (node, pos) => {
    if (pos < from || ['bulletList', 'orderedList'].includes(node.type.name)) {
      return true;
    } else if (node.isInline || ['tableRow', 'tableCell'].includes(node.type.name)) {
      return false;
    }

    if (node.attrs.track) {
      // Skip for now.
    }

    if (node.type.name === 'table') {
      // A table was inserted. We don't add track marks to elements inside of it.
      return false;
    }
  });

  return insertionMark;
};
